import { Accordion, AccordionDetails, AccordionSummary, FormHelperText, InputLabel } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Button } from "@thrivetribe/gloji.ui.atoms.button";
import { ErrorMessage, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router";
import { update_signup_form_values } from "../../../../store/actions/signupFlowData";
import FormikInputOnly from "../../../atoms/formik-input-only";
import { FormikField } from "../../../atoms/FormikField";
import usePostcodeCheck from "../../../hooks/usePostcodeCheck";
import { EligibilityCheckerQuestionsProps, ProgrammeProps } from "../Props";
import { useGetProfileServiceEligibilityCheckQuery } from "../../../../redux/profileApiV3";
import useUserId from "../../../hooks/useUserId";
import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  ModalPageBody,
  ModalPageFooter,
  ModalPageHeader,
  ModalPageHeading,
  ModalPageWrapper,
} from "../../../organisms/ModalPage";
import { PostcodeValidationSchema } from "../../../../data/Validation/EligibilityCheckerValidation";
import { LoadingPage } from "../LoadingPage";

export interface PostcodeCheckerData {
  successScreen: { title: string; heading: string; body: string };
  ineligibleScreens: {
    ineligiblePostcode: { title: string; heading: string; body: string };
    noSpacesAvailable: { title: string; heading: string; body: string };
    noReturnPeriod: { title: string; heading: string; body: string };
  };
  loadingScreen: { title: string; body: string };
  questions: EligibilityCheckerQuestionsProps[];
}
export interface PostcodeCheckerProps extends ProgrammeProps {
  postcodePageData: PostcodeCheckerData;
  programmeId: string;
  existingPostcode: string | null;
}

const PostcodeChecker: React.FC<PostcodeCheckerProps> = (props: PostcodeCheckerProps) => {
  const { programmeTitle, postcodePageData, programmeId, existingPostcode } = props;
  const [userId] = useUserId();
  const [serviceEligibilityFlag, setServiceEligibilityFlag] = useState(false);

  const dispatch = useDispatch();

  const storeCurrentPostcode = useSelector((state: any) => state.signupFlowData.signupFormValues?.postcode);

  const [response, getPostcodeChecked] = usePostcodeCheck();

  const serviceEligibilityResponse = useGetProfileServiceEligibilityCheckQuery(
    serviceEligibilityFlag
      ? {
          userId: userId,
          referringServiceCode: response.data?.code,
          programmeId: programmeId,
        }
      : skipToken
  );

  useEffect(() => {
    if (!!response && response.isPostcodeEligible && !!programmeId && !!userId) {
      setServiceEligibilityFlag(true);
    }
  }, [response, programmeId, userId]);

  const [showLoadingPage, setShowLoadingPage] = useState(false);
  const history = useHistory();
  const { path } = useRouteMatch();

  const initialValues = { postcode: (storeCurrentPostcode ?? existingPostcode ?? "").toUpperCase() };

  useEffect(() => {}, []);

  useEffect(() => {
    window.gtag("event", "page_view", {
      page_title: `Postcode | ${programmeTitle} Eligibility checker | Sign up | Gloji`,
    });
  }, [programmeTitle]);

  useEffect(() => {
    if (response.isPostcodeEligible !== null && !serviceEligibilityResponse.isLoading) {
      if (response.data !== null && !response.data.anySpacesAvailable) {
        history.push(`${path}/no-places-available`);
        return;
      }
      if (response.isPostcodeEligible === true) {
        const serviceCode = response.data.code;
        const serviceId = response.data.id;
        const eligibilityCriteria = response.data.eligibilityCriteria;

        if (
          userId != null &&
          serviceEligibilityResponse.data != null &&
          !serviceEligibilityResponse.data.isEligible
        ) {
          history.push(`/postcode/no-return-period/${serviceEligibilityResponse.data.eligibleReturnDate}`);
        } else {
          dispatch(update_signup_form_values({ serviceCode, serviceId, eligibilityCriteria }));
          // Show success screen
          history.push(`/postcode/success`);
        }
      } else {
        history.push(`/postcode/fail`);
        //Trigger google analytics event tag
        window.gtag("event", "ineligible_postcode", {
          event_category: "postcode",
          event_label: storeCurrentPostcode,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLoadingPage, serviceEligibilityResponse.isLoading]);

  const handleSubmit = async values => {
    dispatch(update_signup_form_values(values));
    setShowLoadingPage(true);
    getPostcodeChecked(values.postcode);
  };

  return (
    <>
      {showLoadingPage === true ? (
        <LoadingPage
          isLoading={response.loading}
          callback={() => setShowLoadingPage(false)}
          heading={postcodePageData?.loadingScreen.title ?? ""}
          text={postcodePageData?.loadingScreen.body ?? ""}
        />
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={PostcodeValidationSchema}
          validateOnChange={true}
        >
          {({ isSubmitting, values }) => (
            <Form className="eligibility-checker-form">
              <Helmet>
                <title>Postcode | {programmeTitle} | Eligibility checker | Sign up | Gloji</title>
              </Helmet>
              <ModalPageWrapper>
                <ModalPageBody className="with-sticky-footer">
                  <ModalPageHeader
                    backButtonOnClick={() => {
                      history.goBack();
                    }}
                  >
                    Checking we're a good fit
                  </ModalPageHeader>
                  <ModalPageHeading size="large">{postcodePageData?.questions[0].question}</ModalPageHeading>
                  <InputLabel htmlFor="postcode" className="eligibility-checker__input-label">
                    {postcodePageData?.questions[0].label}
                  </InputLabel>
                  <FormikField
                    className="eligibility-checker__text-input"
                    id="postcode"
                    name="postcode"
                    component={FormikInputOnly}
                    placeholder="e.g EC2M 3TQ"
                  />
                  <ErrorMessage className="Mui-error" component={FormHelperText} name="postcode" />
                  <Accordion square={true} elevation={0} className="eligibility-checker__accrodion">
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon fontSize="large" />}
                      aria-controls={`postcode-question-explanation`}
                    >
                      <span>{postcodePageData?.questions[0].whyQuestion}</span>
                    </AccordionSummary>
                    <AccordionDetails>
                      <span>{postcodePageData?.questions[0].whyQuestionDetails}</span>
                    </AccordionDetails>
                  </Accordion>
                </ModalPageBody>
                <ModalPageFooter>
                  <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                    Continue
                  </Button>
                </ModalPageFooter>
              </ModalPageWrapper>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default PostcodeChecker;
